import { graphql } from 'gatsby';

import Hero from '@klaviyo-mkt/gatsby-components/organisms/hero';
import Logos from '@klaviyo-mkt/gatsby-components/organisms/logos';

import theme from '@klaviyo-mkt/gatsby-components/shared/styles/themes/default';

import GlobalHead from '../layout/head';
import Layout from '../layout';

import { useAffiliatePageTemplateQuery } from '../../hooks/useAffiliatePageTemplateQuery';

import { Wrapper } from '@klaviyo-mkt/gatsby-components/utils/wrapper';
import ContentCarousel from '@klaviyo-mkt/gatsby-components/organisms/content-carousel';
import FullColumn from '@klaviyo-mkt/gatsby-components/organisms/full-column';
import JumpAnchor from '@klaviyo-mkt/gatsby-components/atoms/jump-anchor';
import Pricing from '@klaviyo-mkt/gatsby-components/organisms/pricing';
import Video from '@klaviyo-mkt/gatsby-components/organisms/video';

import type { HeadProps, PageProps } from 'gatsby';
import type { IAffiliateData, IAffiliatePageContext, ICarouselDefaultValues } from './types';
import type { IContentCarouselProps } from '@klaviyo-mkt/gatsby-components/organisms/content-carousel/types';
import type { THeroBackground } from '@klaviyo-mkt/gatsby-components/organisms/hero/types';


const AffiliateTemplate = ({ data, pageContext, ...pageProps }: PageProps<IAffiliateData, IAffiliatePageContext>) => {
  const affiliateTemplateData = useAffiliatePageTemplateQuery();

  if (Object.keys(affiliateTemplateData).length === 0) return null;
  
  const {
    badges,
    bodyCopy: defaultBodyCopy,
    customers: defaultCustomers,
    demoVideo,
    customerLogos,
    formCopy: defaultFormCopy,
    formHeadline: defaultFormHeadline,
    fullColumn,
    headline: defaultHeadline,
    image: defaultImage,
    anchor,
    pricing,
    resources: defalutResources,
    shortTitle: defaultShortTitle,
  } = affiliateTemplateData || {};

  const { wpAffiliatePage } = data || {};
  const { backgroundColor, customPadding } = wpAffiliatePage?.affiliatePage;
  const { affiliatePage, navigationColor, translations } = wpAffiliatePage || {};
  const { shortTitle } = pageContext || {};

  // Override default values with values from the Posttype - Affiliate child pages
  const { bodyCopy, formCopy, formHeadline, headline, image, logos: logoGroup, resourceCarousel, customerCarousel } = affiliatePage || {};
  
  const { logos = [] } = logoGroup || {};
  const filteredLogos = logos.filter((logo) => logo.image !== null);
  
  const hasNonFalsyValues = (obj: IContentCarouselProps, ignoredKeys: ICarouselDefaultValues) => {
    for (const key in obj) {
      if (obj.hasOwnProperty(key) && !ignoredKeys.hasOwnProperty(key)) {
        const value = obj[key as keyof IContentCarouselProps];
        if (value) { 
          return true;
        }
      }
    }
  };

  const carouselDefaultValues = {
    alignment: 'alignment',
    backgroundColor: 'backgroundColor',
    carouselBehavior: 'carouselBehavior',
    headerFont: 'headerFont',
    featureCard: 'featureCard',
  };

  const customer = (customerCarousel && hasNonFalsyValues(customerCarousel, carouselDefaultValues)) ? customerCarousel : defaultCustomers.contentCarousel;
  const resources = (resourceCarousel && hasNonFalsyValues(resourceCarousel, carouselDefaultValues)) ? resourceCarousel : defalutResources.contentCarousel;
  
  return (
    <Layout
      {...pageProps}
      {...pageContext}
      path={pageProps.location.pathname}
      navigationColor={navigationColor}
      navigationStyle='minimal'
      shortTitle={shortTitle || defaultShortTitle}
      templateName="Default"
      theme={theme}
      translations={translations}
      heroBackground={backgroundColor as THeroBackground}
    >
      {anchor?.jumpAnchor?.id && <JumpAnchor id={anchor?.jumpAnchor?.id} />}
      <Wrapper backgroundColor={backgroundColor} customPadding={customPadding}>
        <Hero
          background={backgroundColor as THeroBackground}
          bodyCopy={bodyCopy  || defaultBodyCopy}
          demoFormSettings={{ redirectUrl: '' }}
          embeddedForm='signup'
          embeddedFormHeadline={formHeadline || defaultFormHeadline}
          embeddedFormBodyCopy={formCopy || defaultFormCopy}
          headline={headline || defaultHeadline}
          image={image || defaultImage}
          imageType='vertical'
          pricingVariant={false}
          textAlignment='left'
        />

      </Wrapper>
        <Pricing {...pricing.pricing} />
        

        {/* Customer Logos */}
        <Logos
          {...customerLogos?.logosOrganism}
          logos={filteredLogos?.length > 0 ? filteredLogos : customerLogos?.logosOrganism?.logos}
        />
        <FullColumn {...fullColumn.fullColumn}  />
        {/* Demo Video */}
        <Video  {...demoVideo.video} />
        
        {/* Customer */}
        <ContentCarousel
          {...customer}
        />

        {/* Resxources  */}
        <ContentCarousel {...resources} />
        
        {/* Badges */}
        {badges?.logosOrganism && badges?.logosOrganism?.logos?.length > 0 && (
          <Logos
            {...badges.logosOrganism}
          />
        )}
    </Layout>
  );
};

export const query = graphql`
  query AffiliatePageTemplate(
    $id: String!,
  ) {
    wpAffiliatePage(id: {eq: $id}) {
      ...AffiliatePageDetails
    }
  }
`;

export const Head = ({
  data,
  location,
}: HeadProps<IAffiliateData, IAffiliatePageContext>) => (
  <GlobalHead
    isFrontPage={false}
    pathname={location.pathname}
    seo={data?.wpAffiliatePage?.headlessSeo}
    translations={data?.wpAffiliatePage?.translations}
  >
    {data?.wpAffiliatePage?.headlessSeo?.jsonLd ? (
      <script type="application/ld+json">{data?.wpAffiliatePage?.headlessSeo?.jsonLd}</script>
    ) : null}
  </GlobalHead>
);

export default AffiliateTemplate;
